import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { ComponentProps } from 'react';
import styled from 'styled-components';
import {
  ResourceLibraryItem, ResourceLibraryPage, WistiaOEmbedData,
} from '../../lib/types';
import Footer from '../modules/Footer';
import { renderModules } from '../Page';
import Card from './cards/Card';
import CardGrid from './cards/CardGrid';
import ModulePaddingWrapper from './ModulePaddingWrapper';
import Navbar from './Navbar';
import PageMeta from './PageMeta';
import ResourceFiltering from './ResourceFiltering';

export type ResourceLibraryProps = {
    pageCount: number,
    currentPage: number,
    currentResources: (ResourceLibraryItem & { wistiaData?: WistiaOEmbedData })[],
    navbarData: ComponentProps<typeof Navbar>,
    filterProps: Omit<ComponentProps<typeof ResourceFiltering>, 'onChange'>,
    query: ParsedUrlQuery,
    resourceLibraryProps: ResourceLibraryPage,
}

function ResourceLibraryItem({
  description, title, type, assetResource, linkedResource,
  image, wistiaVideoId, wistiaData, thumbnailPosition,
}: ResourceLibraryProps['currentResources'][0]) {
  let newImage = image;
  if (wistiaData) {
    newImage = {
      url: wistiaData.thumbnail_url,
      height: wistiaData.thumbnail_height,
      width: wistiaData.thumbnail_width,
      description: wistiaData.title,
      title: wistiaData.title,
      contentType: '',
    };
  }

  let link = linkedResource || '#';
  if (assetResource) {
    link = {
      title,
      url: assetResource.url,
      newTab: true,
    };
  }

  if (wistiaVideoId) {
    link = {
      title,
      wistiaVideoId,
      newTab: false,
    };
  }

  return (
    <Card
      image={newImage || null}
      link={link}
      tags={[{ content: type }]}
      title={title}
      imagePosition={thumbnailPosition}
      mediumFlip
    >
      {description}
    </Card>
  );
}

const EmptySearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  padding: 38px;
  color: var(--color-charcoal-v50);

  p {
    font-size: 21px;
    margin: 0;
    text-align: center;
    max-width: 280px;
  }
`;

export const NoSearchResults = () => (
  <EmptySearchWrapper>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 36 36"
      width="36"
    >
      <path
        d="M10.41 11.09a2 2 0 0 1 2.83 0l2.48 2.47 2.47-2.47a2 2 0 0 1 2.83 2.82l-2.47 2.48 2.47 2.47a2 2 0 0 1-2.83 2.83l-2.47-2.47-2.48 2.47a2 2 0 1 1-2.83-2.83l2.48-2.47-2.48-2.48a2 2 0 0 1 0-2.82Z"
      />
      <path
        d="M16 0a16 16 0 1 0 10.05 28.45 2 2 0 0 0 .54.96l6 6a2 2 0 1 0 2.82-2.82l-6-6a2 2 0 0 0-.96-.54A16 16 0 0 0 16 0ZM4 16a12 12 0 1 1 24 0 12 12 0 0 1-24 0Z"
      />
    </svg>
    <p>
      Unfortunately, your search
      didn’t return any results.
    </p>
  </EmptySearchWrapper>
);

const parsedQueryToSearchParams = (query: ParsedUrlQuery) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(
    ([k, v]) => (
      v && (Array.isArray(v) ? v.forEach((j) => params.append(k, j)) : params.append(k, v))
    ),
  );
  return params;
};

const getParams = (
  selectedTypes: string[],
  perPage: number,
  query: string,
  resetPage: boolean = false,
) => {
  const searchParams = new URLSearchParams(window?.location.search);
  searchParams.set('count', perPage.toString());
  searchParams.set('q', query);
  searchParams.delete('type');
  if (resetPage) {
    searchParams.delete('page');
  }
  selectedTypes.forEach((type) => searchParams.append('type', type));

  const qs = searchParams.toString();
  return qs && `?${qs}`;
};

const ResourceLibrary = ({
  pageCount,
  currentPage,
  currentResources,
  navbarData,
  resourceLibraryProps,
  filterProps,
  query,
} : ResourceLibraryProps) => {
  const params = parsedQueryToSearchParams(query);
  params.delete('page');
  const router = useRouter();

  const getParamsForPage = (page: number) => {
    const newParams = new URLSearchParams(params);
    if (page > 1) {
      newParams.set('page', page.toString());
    }
    const paramStr = newParams.toString();
    return paramStr.length ? `?${paramStr}` : '';
  };

  const handleResourceFilterChange = (selectedTypes: string[], perPage: number, q: string) => {
    const newParams = getParams(selectedTypes, perPage, q, true);
    const newUrl = `/resources${newParams}`;
    void router.push(newUrl, undefined, { scroll: false });
  };

  const getPageUrl = (pageNumber: number) => (`/resources${getParamsForPage(pageNumber)}`);

  // Make navbar transparent for all rendered modules
  const navbarProps = {
    ...navbarData,
    transparent: true,
  };
  const topModules = renderModules(resourceLibraryProps.topModules, navbarProps);
  const bottomModules = renderModules(resourceLibraryProps.bottomModules, navbarProps);
  return (
    <div>
      <PageMeta {...resourceLibraryProps} imageUrl={resourceLibraryProps.ogImage?.url} slug="/resources" />
      {topModules}
      <ModulePaddingWrapper theme={resourceLibraryProps.theme}>
        <ResourceFiltering onChange={handleResourceFilterChange} {...filterProps} />
        {currentResources.length ? (
          <CardGrid
            columns={3}
            pagination={{
              page: currentPage,
              getPageUrl,
              nextText: 'Next',
              prevText: 'Previous',
              totalPages: pageCount,
            }}
          >
            {currentResources.map((resource) => (
              <ResourceLibraryItem key={resource.sys.id} {...resource} />
            ))}
          </CardGrid>
        ) : <NoSearchResults />}
      </ModulePaddingWrapper>
      {bottomModules}
      <Footer {...navbarData} />
    </div>
  );
};

export default ResourceLibrary;
